import React, { useEffect, useState, useCallback, useRef } from "react";
import useOutsideClick from "../../hooks/useOutsideClick";
import { useMessage } from "../../context/MessageContext";
import { GENERAL_ERROR_MESSAGE } from "../../utils/message";
import { getApi } from "../../apis/api";
import { XCrossIcon } from "../../assets";
import { VALIDATION_FOCUS_ATTR } from "../../utils/constants";
import "../../assets/scss/abstracts/_form.scss";

export interface SingleDropdownItemType {
  id: string;
  name: string;
  type?: string;
  agentType?: string;
  dickersonAgent?: boolean;
}

interface SingleDropdownProps {
  fieldName: string;
  selectedItem: SingleDropdownItemType | null;
  setSelectedData: (
    fieldName: string,
    item: SingleDropdownItemType | null,
  ) => void;
  apiUrl?: string;
  staticData?: SingleDropdownItemType[];
  placeholder?: string;
  limit?: number;
  disable?: boolean;
  mandatory?: boolean;
  labelTitle?: string;
  extendedUrl?: Record<string, string>;
  keyBindName?: string;
  enableRemove?: boolean;
  type?: string;
  agentId?: string;
  status?: number;
  placeholderMandatoryMark?: boolean;
  customStyle?: any;
  isFocused?: boolean;
  autoOpenOnFocus?: boolean;
  prependItems?: SingleDropdownItemType[];
  /**
   * Identifies this dropdown to the mandatory-field validation, which locates controls
   * by `data-field` and focuses the caption when a required selection is missing.
   */
  fieldKey?: string;
  /** Validation message rendered beneath the control. */
  error?: string;
}

const SingleDropdown: React.FC<SingleDropdownProps> = ({
  fieldName,
  selectedItem,
  setSelectedData,
  apiUrl,
  staticData,
  placeholder = "-Select-",
  limit = 15,
  disable,
  mandatory,
  labelTitle,
  extendedUrl,
  keyBindName = "name",
  enableRemove,
  type,
  agentId,
  status = 1,
  placeholderMandatoryMark,
  customStyle,
  isFocused,
  autoOpenOnFocus,
  prependItems,
  fieldKey,
  error,
}) => {
  const { setMessage } = useMessage();
  const [data, setData] = useState<SingleDropdownItemType[]>(staticData || []);
  const allItems = [...(prependItems || []), ...data];
  const [isOpened, setIsOpened] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [keyword, setKeyword] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isSearchVisible, setIsSearchVisible] = useState<boolean>(false);
  const [isSelected, setIsSelected] = useState<number | null>(null);
  const [isDivFocused, setIsDivFocused] = useState(false);
  const skipFocusOpenRef = useRef(false);
  const captionRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Detect outside clicks to close the dropdown
  const outsideClickRef = useOutsideClick<HTMLDivElement>(() => {
    setIsOpened(false);
    setIsSearchVisible(false);
  });

  const handleOpened = () => {
    if (!disable) {
      setIsOpened(!isOpened);
      setIsSearchVisible(!isSearchVisible);
    }
  };

  const handleDoubleClick = () => {
    if (!disable) {
      setIsOpened(true);
      setIsSearchVisible(true);
    }
  };

  const handleRemoveSelection = () => {
    setSelectedData(fieldName, null);
    setIsOpened(false);
  };

  const fetchDropdownData = useCallback(
    async (reset: boolean = false) => {
      if (!apiUrl || isLoading) return;

      try {
        setIsLoading(true);
        let url = `${apiUrl}?limit=${limit}&page=${reset ? 1 : page}`;
        if (keyword) {
          url += `&keyword=${keyword}`;
        }
        if (type) {
          url += `&type=${type}`;
        }
        if (agentId) {
          url += `&agentId=${agentId}`;
        }
        if ([1, 0]?.includes(status)) {
          url += `&status=${status}`;
        }

        const extendedUrlParams = Object.entries(extendedUrl || {})
          .map(
            ([key, value]) =>
              `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
          )
          .join("&");

        if (extendedUrlParams) {
          url += `&${extendedUrlParams}`;
        }

        const response = await getApi(url);
        if (response.success) {
          const fetchedData: any = response.data || [];
          const mappedData = fetchedData.map((item: any) => ({
            id: item?.id,
            name: item?.[keyBindName],
            type: item?.type,
            agentType: item?.agentType?.name,
            dickersonAgent: item?.dickersonAgent,
          }));
          setData(reset ? mappedData : [...data, ...mappedData]);
          setHasMore(mappedData.length >= limit);
        } else {
          setMessage(response.error?.message || GENERAL_ERROR_MESSAGE, "error");
        }
      } catch (error) {
        setMessage(GENERAL_ERROR_MESSAGE, "error");
      } finally {
        setIsLoading(false);
      }
    },
    [apiUrl, keyword, page, limit, extendedUrl, data, isLoading, setMessage],
  );

  useEffect(() => {
    if (staticData?.length) {
      setData(staticData);
    }
  }, [staticData]);

  useEffect(() => {
    if (keyword) {
      if (staticData) {
        if (keyword) {
          const newData = staticData.filter((item) =>
            item.name.toLowerCase().includes(keyword.toLowerCase()),
          );
          setData(newData);
        } else {
          setData(staticData);
        }
      } else {
        const debounceFetch = setTimeout(() => {
          fetchDropdownData(true);
        }, 1000);
        return () => clearTimeout(debounceFetch);
      }
    } else if (staticData) {
      setData(staticData);
    } else if (apiUrl) {
      fetchDropdownData(true);
    }
  }, [apiUrl, keyword]);

  useEffect(() => {
    if (page > 1) {
      fetchDropdownData();
    }
  }, [page]);

  const handleScroll = () => {
    if (dropdownRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = dropdownRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 10 && hasMore) {
        setPage((prev) => prev + 1);
      }
    }
  };

  useEffect(() => {
    if (dropdownRef.current) {
      dropdownRef.current.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (dropdownRef.current) {
        dropdownRef.current.removeEventListener("scroll", handleScroll);
      }
    };
  }, [hasMore]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpened) return;
    switch (e.key) {
      case "Tab":
        // Close dropdown and move focus to next form element
        e.preventDefault();
        setIsOpened(false);
        setIsSearchVisible(false);
        setKeyword("");
        setIsSelected(null);
        skipFocusOpenRef.current = true;
        // Move focus to the next focusable element in the form
        const formElements = Array.from(
          document.querySelectorAll<HTMLElement>(
            'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]:not([disabled])',
          ),
        ).filter((el) => el.offsetParent !== null);
        const currentContainer = outsideClickRef.current;
        if (currentContainer) {
          const currentIndex = formElements.findIndex(
            (el) =>
              currentContainer.contains(el) &&
              (el === document.activeElement || el === e.target),
          );
          if (currentIndex !== -1) {
            // Find the next focusable element that is outside this dropdown
            for (let i = currentIndex + 1; i < formElements.length; i++) {
              if (!currentContainer.contains(formElements[i])) {
                formElements[i].focus();
                break;
              }
            }
          }
        }
        break;
      case "ArrowDown":
        e.preventDefault();
        if (!allItems || allItems.length === 0) return;
        if (isSelected === null || isSelected === allItems.length - 1) {
          setIsSelected(0);
        } else {
          setIsSelected((prev) => (prev! + 1) % allItems.length);
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (!allItems || allItems.length === 0) return;
        if (isSelected === null || isSelected === 0) {
          setIsSelected(allItems.length - 1);
        } else {
          setIsSelected(
            (prev) => (prev! - 1 + allItems.length) % allItems.length,
          );
        }
        break;
      case "Enter":
        e.preventDefault();
        // If user navigated with arrows, select highlighted item
        if (isSelected !== null && allItems[isSelected]) {
          setSelectedData(fieldName, allItems[isSelected]);
          setIsOpened(false);
          setIsSearchVisible(false);
          setKeyword("");
          setIsSelected(null);
          skipFocusOpenRef.current = true;
          setTimeout(() => {
            if (captionRef.current) {
              captionRef.current.setAttribute("data-suppress-focus", "");
              captionRef.current.focus();
            }
          }, 0);
        } else if (keyword && data.length > 0) {
          // Otherwise, only select if typed text exactly matches an item
          const exactMatch = data.find(
            (item) => item.name.toLowerCase() === keyword.toLowerCase(),
          );
          if (exactMatch) {
            setSelectedData(fieldName, exactMatch);
            setIsOpened(false);
            setIsSearchVisible(false);
            setKeyword("");
            setIsSelected(null);
            skipFocusOpenRef.current = true;
            setTimeout(() => {
              if (captionRef.current) {
                captionRef.current.setAttribute("data-suppress-focus", "");
                captionRef.current.focus();
              }
            }, 0);
          }
        }
        break;
      default:
        break;
    }
  };
  // useEffect(() => {
  //   if (isFocused === true){
  //     if(!disable){
  //       setIsOpened(true);
  //       setIsSearchVisible(true);
  //     }
  //   }
  //   else {
  //     setIsOpened(false);
  //     setIsSearchVisible(false);
  //   }
  // }, [isFocused]);
  // Auto-scroll to highlighted item during keyboard navigation
  useEffect(() => {
    if (isSelected !== null && isOpened && dropdownRef.current) {
      const container = dropdownRef.current;
      const highlightedItem = container.querySelector(
        ".highlighted",
      ) as HTMLElement;
      if (highlightedItem) {
        const containerTop = container.scrollTop;
        const containerBottom = containerTop + container.clientHeight;
        const itemTop = highlightedItem.offsetTop;
        const itemBottom = itemTop + highlightedItem.clientHeight;

        if (itemTop < containerTop) {
          container.scrollTop = itemTop;
        } else if (itemBottom > containerBottom) {
          container.scrollTop = itemBottom - container.clientHeight;
        }
      }
    }
  }, [isSelected, isOpened]);

  // Auto-highlight selected item when dropdown opens (but not while typing)
  const hasAutoHighlightedRef = useRef(false);
  useEffect(() => {
    if (!isOpened) {
      hasAutoHighlightedRef.current = false;
      return;
    }
    // Only auto-highlight once per open cycle
    if (hasAutoHighlightedRef.current) return;
    if (selectedItem && allItems.length > 0 && !keyword) {
      const selectedIndex = allItems.findIndex(
        (item) => String(item.id) === String(selectedItem.id),
      );
      if (selectedIndex !== -1) {
        setIsSelected(selectedIndex);
        hasAutoHighlightedRef.current = true;
      }
    }
  }, [isOpened, data, selectedItem, keyword]);

  // Modify the handleFocus function or create a new handler
  const handleCaptionFocus = (e: React.FocusEvent) => {
    // Skip re-opening and highlighting if focus returned here after Enter selection
    if (skipFocusOpenRef.current) {
      skipFocusOpenRef.current = false;
      e.stopPropagation(); // Prevent parent handleFocus from setting border
      return;
    }
    // A disabled control is still a tab stop, but it must not read as active: suppress
    // both the wrapper tint and the caption's own :focus border. Stopping propagation
    // matters — otherwise focus bubbles to the wrapper's handleFocus, which sets the
    // same flag. handleBlur clears the attribute again.
    if (disable) {
      captionRef.current?.setAttribute("data-suppress-focus", "");
      e.stopPropagation();
      return;
    }
    // Mandatory-field validation focuses this control to point at its error. Opening the
    // list would cover that error, and the caption's grey :focus border outranks the red
    // one — so stay closed and suppress the focus styling, leaving the error visible.
    if (captionRef.current?.hasAttribute(VALIDATION_FOCUS_ATTR)) {
      captionRef.current.removeAttribute(VALIDATION_FOCUS_ATTR);
      captionRef.current.setAttribute("data-suppress-focus", "");
      e.stopPropagation();
      return;
    }
    setIsDivFocused(true);
    // Open dropdown when focused via Tab key
    if (!isOpened) {
      setIsOpened(true);
      setIsSearchVisible(true);
    }
  };

  const handleFocus = () => {
    if (skipFocusOpenRef.current) return;
    setIsDivFocused(true);
  };
  // const handleBlur = () => setIsDivFocused(false);
  const handleBlur = (e: React.FocusEvent) => {
    setIsDivFocused(false);
    captionRef.current?.removeAttribute("data-suppress-focus");
    if (!autoOpenOnFocus) return; // <--- don't auto-close for other screens

    // Check if the new focus target is still within the dropdown container
    // Use setTimeout to allow focus to move between dropdown elements
    setTimeout(() => {
      if (
        outsideClickRef.current &&
        !outsideClickRef.current.contains(document.activeElement)
      ) {
        // Focus has moved outside the dropdown, close it
        setIsOpened(false);
        setIsSearchVisible(false);
        setKeyword("");
        setIsSelected(null);
      }
    }, 0);
  };

  return (
    <div className={`form__Field`} ref={outsideClickRef}>
      {labelTitle && (
        <label className="form__label">
          {labelTitle} {mandatory && <span className="mandatory-mark">*</span>}
        </label>
      )}
      <div
        onFocus={handleFocus} onBlur={handleBlur}
        data-field={fieldKey}
        className={`customSelect ${isDivFocused ? "form__Field--focused" : ""} ${isOpened ? "open" : ""} ${error ? "customSelect--error" : ""}`}
        ref={outsideClickRef}
        // onKeyDown={handleKeyDown}
      >
        {isSearchVisible && (apiUrl || staticData) ? (
          <input
            type="text"
            placeholder="Search..."
            value={keyword}
            onChange={(e) => {
              setKeyword(e.target.value);
              setIsSelected(null); // Reset selection when typing
            }}
            onKeyDown={handleKeyDown}
            className="form__input form__input--search form__input--sm"
            autoFocus
          />
        ) : (
          <div
            ref={captionRef}
            tabIndex={0}
            className={`customSelect__caption ${
              disable ? "customSelect__caption--disabled" : ""
            }`}
            onClick={handleOpened}
            onFocus={handleCaptionFocus}
            // onKeyDown={(e) => {
            //   // Handle Space or Enter to open dropdown
            //   if (e.key === " " || e.key === "Enter") {
            //     e.preventDefault();
            //     if (!disable && !isOpened) {
            //       handleOpened();
            //     }
            //   }
            // }
            // }
            onKeyDown={handleKeyDown}
            //onDoubleClick={handleDoubleClick}
          >
            {selectedItem && selectedItem.name ? (
              selectedItem.name
            ) : (
              <span className="customSelect__caption__placeholder">
                {placeholder}{" "}
                {placeholderMandatoryMark ? (
                  <span className="mandatory-mark">*</span>
                ) : (
                  ""
                )}
              </span>
            )}

            {selectedItem && selectedItem.name && enableRemove && !disable && (
              <span
                style={{
                  background: "#fff",
                  position: "absolute",
                  right: "8px",
                  top: "10px",
                }}
                onClick={() => {
                  handleRemoveSelection();
                }}
              >
                <XCrossIcon />
              </span>
            )}
          </div>
        )}
        <div
          className="customSelect__dropdown"
          ref={dropdownRef}
          style={customStyle ? customStyle : {}}
        >
          <div className="customSelect__dropdown__body">
            {isLoading && page === 1 ? (
              <div className="customSelect__dropdown__noitem">
                <div className="col">Loading...</div>
              </div>
            ) : allItems.length === 0 ? (
              <div className="customSelect__dropdown__noitem">
                <div className="col">No data found.</div>
              </div>
            ) : (
              allItems.map((item, index) => (
                <div
                  className={`customSelect__dropdown__item ${
                    selectedItem?.id != null &&
                    String(selectedItem.id) === String(item.id)
                      ? "selected"
                      : ""
                  } ${isSelected === index ? "highlighted" : ""}`}
                  onClick={() => {
                    setSelectedData(fieldName, item);
                    setIsOpened(false);
                    setIsSearchVisible(false);
                    setKeyword("");
                  }}
                  key={item.id}
                >
                  {item.name}
                </div>
              ))
            )}
            {isLoading && page > 1 && (
              <div className="customSelect__dropdown__noitem">
                <div className="col">Loading more...</div>
              </div>
            )}
          </div>
        </div>
      </div>
      {error && <span className="form__error">{error}</span>}
    </div >
  );
};

export default SingleDropdown;
